import { useDispatch, useSelector } from 'react-redux'
import reactStringReplace from 'react-string-replace'
import { Icon } from 'semantic-ui-react'

import { Tournament } from 'search'
import System from 'system'
import { DropZone, ProgressBar } from 'ui'

import { actions, selectors } from '../redux'

import type { FixtureSchema, InstitutionSchema } from 'institutions'
import type { FC, ReactNode } from 'react'

export interface FixtureProps {
  institution: InstitutionSchema
}

const toAccordionList = (
  title: string,
  data: FixtureSchema['actions'] | Record<string, string | string[]> | string[],
) => {
  const childs: ReactNode[] = []
  // @ts-ignore
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === 'string') {
      // Match URLs
      let text = reactStringReplace(value, /(https?:\/\/\S+)/g, (match, i) => (
        <a key={match + i} href={match} rel="noopener noreferrer" target="_blank">
          ...{match.split('/').pop()?.split('-mas-').pop()?.split('-fem-').pop() || 'link'}
        </a>
      ))

      text = reactStringReplace(text, /(Found)/g, (match, i) => <Icon key={match + i} color="green" name="check" />)

      text = reactStringReplace(text, /(Approved Match)/g, (match, i) => (
        <Icon key={match + i} color="black" name="lock" />
      ))

      text = reactStringReplace(text, /(Approving Match)/g, (match, i) => (
        <Icon key={match + i} color="orange" name="lock" />
      ))

      text = reactStringReplace(text, /(Blocked Match)/g, (match, i) => (
        <b key={match + i}>
          <Icon color="red" name="ban" /> Bloqueado:{' '}
        </b>
      ))

      text = reactStringReplace(text, /(Adjusting Match)/g, (match, i) => (
        <b key={match + i}>
          <Icon color="orange" name="asterisk" /> Ajustado:{' '}
        </b>
      ))

      text = reactStringReplace(text, /(Unmodified Match)/g, (match, i) => (
        <Icon key={match + i} color="green" name="checkmark box" />
      ))

      text = reactStringReplace(text, /(Remove)/g, (match, i) => <Icon key={match + i} color="red" name="remove" />)

      childs.push(
        <div key={key} className="border-l border-text px-2 ml-1">
          {text}
        </div>,
      )
    } else {
      childs.push(toAccordionList(key, value))
    }
  })

  return (
    <details key={title} className="border-l px-2 ml-1">
      <summary className="font-bold cursor-pointer">{title}</summary>
      <div className="!m-0 whitespace-pre-wrap overflow-hidden">{childs}</div>
    </details>
  )
}

const Fixture: FC<FixtureProps> = ({ institution }) => {
  const dispatch = useDispatch()
  const fixtures = useSelector(selectors.fixtures)

  const uploadFiles = (files: File[]) => {
    for (const fixture of files) {
      dispatch(actions.fixturesUpload({ fixture, institutionId: institution.id }))
    }
  }

  const inProgress =
    fixtures.data.filter(({ loaded }) => loaded).length + fixtures.data.filter(({ loading }) => loading).length
  const total = fixtures.data.length * 2 || 1
  const totalProgress = (inProgress / total) * 100

  return (
    <div className="Fixture bg-white p-2 rounded">
      <div className="text-xl font-bold">Carga de archivo/fixture excel</div>
      <div className="m-2 p-2 rounded-lg border border-orangeLight text-sm bg-orangeLightest">
        Las acciones con este archivo son difícilmente reversibles, por lo que te recomendamos que antes de subirlo
        revises todos los datos.
      </div>
      <div className="text-lg pt-2 font-bold">1. Descarga el archivo base y llénalo:</div>
      <div className="m-2 px-2 text-sm">
        <a href={`${System.services}/templates/fixture?rnd=${Math.random()}`} rel="noreferrer" target="_blank">
          <span className="flex items-center gap-2">
            <Icon className="!m-0" name="file excel outline" />
            Descargar
          </span>
        </a>
      </div>
      <div className="text-lg pt-2 font-bold">2. Carga el/los archivo(s) lleno(s):</div>
      <div className="m-2 px-2 text-sm">
        <DropZone
          accept={{
            'application/vnd.ms-excel': ['.xls', '.xlsx'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xls', '.xlsx'],
          }}
          className="text-sm"
          onDrop={uploadFiles}
          useRootActions
        />
      </div>
      <div className="text-lg pt-2 font-bold">3. Verifica el estado de la carga:</div>
      <div className="flex flex-col gap-2 m-2 px-2">
        {fixtures.loading && <ProgressBar value={totalProgress} />}
        {fixtures.data.map((fixture, index) => (
          <div key={fixture.key} className="rounded border">
            <div className="flex gap-2 border-b p-2 font-bold">
              <div>{index + 1}.</div>
              <div className="grow flex flex-col gap-1 items-start">
                <div>{fixture.key}</div>
                {fixture.data[0]?.tournament && (
                  <Tournament className="text-sm" tournament={fixture.data[0]?.tournament} />
                )}
              </div>
            </div>
            {(fixture.loading || !!fixture.error) && (
              <ProgressBar alert={!!fixture.error} className="text-xxs" value={100} />
            )}
            {fixture.error ? (
              <div className="p-2">
                <Icon className="!m-0 text-red" name="warning sign" /> Error {fixture.error.code}
                <div className="p-2 text-sm">
                  {fixture.error.message}
                  {fixture.error.trace && (
                    <details>
                      <summary className="font-bold cursor-pointer">Traza del error</summary>
                      <pre className="!m-0 whitespace-pre-wrap overflow-hidden">{fixture.error.trace}</pre>
                    </details>
                  )}
                </div>
              </div>
            ) : (
              <div className="pb-2">
                {fixture.data[0]?.actions && (
                  <div>
                    <div className="p-2">
                      <Icon className="!m-0 text-green" name="checkmark" /> Cargado correctamente
                    </div>
                    <div className="px-4">{toAccordionList('Acciones', fixture.data[0]?.actions)}</div>
                  </div>
                )}
                {fixture.data[0]?.blocked && (
                  <div>
                    <div className="p-2">
                      <Icon className="!m-0 text-orange" name="warning sign" /> Inconsistencias
                    </div>
                    <div className="px-4">{toAccordionList('Bloqueados', fixture.data[0]?.blocked)}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Fixture
